import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Building2, Lock, Mail, ArrowRight, ShieldCheck, AlertCircle, RefreshCw, UserPlus, KeyRound, Phone, FileText, CheckCircle2, X, Wrench } from 'lucide-react';
import { supabase } from '../supabaseClient';
import { MAINTENANCE_CONFIG } from '../config/maintenance';
import './CompanyLogin.css';

const CompanyLogin = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');

  // Self-Registration Modal State
  const [isRegisterOpen, setIsRegisterOpen] = useState(false);
  const [registerLoading, setRegisterLoading] = useState(false);
  const [registerForm, setRegisterForm] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
    phone: '',
    cuit: ''
  });
  const [registerSuccess, setRegisterSuccess] = useState('');

  // Password Reset Modal State
  const [isResetOpen, setIsResetOpen] = useState(false);
  const [resetLoading, setResetLoading] = useState(false);
  const [resetForm, setResetForm] = useState({
    email: '',
    cuit: '',
    newPassword: '',
    confirmNewPassword: ''
  });
  const [resetSuccess, setResetSuccess] = useState('');

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!email.trim() || !password.trim()) {
      setErrorMsg('Por favor ingrese su correo electrónico y contraseña.');
      return;
    }

    setLoading(true);
    setErrorMsg('');

    try {
      const cleanEmail = email.trim().toLowerCase();
      const cleanPassword = password.trim();

      // Query company by email case-insensitively using ilike
      const { data: companyList, error } = await supabase
        .from('companies')
        .select('*')
        .ilike('email', cleanEmail);

      if (error) throw error;

      // Find company matching email and password_hash
      const data = (companyList || []).find(c => 
        (c.email || '').trim().toLowerCase() === cleanEmail && 
        (c.password_hash || '').trim() === cleanPassword
      );

      if (!data) {
        setErrorMsg('Correo electrónico o contraseña incorrectos.');
        setLoading(false);
        return;
      }

      if (data.is_active === false) {
        setErrorMsg('Esta cuenta de empresa se encuentra inactiva. Contacte a administración.');
        setLoading(false);
        return;
      }

      // Save active company session
      localStorage.setItem('gdt_active_company', JSON.stringify(data));
      navigate('/portal/empresa');
    } catch (err) {
      console.error('Login error:', err);
      setErrorMsg('Error al iniciar sesión. Intente nuevamente.');
    } finally {
      setLoading(false);
    }
  };

  const handleRegisterCompany = async (e) => {
    e.preventDefault();
    setErrorMsg('');
    setRegisterSuccess('');

    if (!registerForm.name.trim() || !registerForm.email.trim() || !registerForm.password.trim()) {
      setErrorMsg('Por favor ingrese el Nombre de la Empresa, Correo Electrónico y Contraseña.');
      return;
    }

    if (registerForm.password !== registerForm.confirmPassword) {
      setErrorMsg('Las contraseñas ingresadas no coinciden.');
      return;
    }

    if (registerForm.password.length < 4) {
      setErrorMsg('La contraseña debe tener al menos 4 caracteres.');
      return;
    }

    setRegisterLoading(true);
    try {
      const cleanEmail = registerForm.email.trim().toLowerCase();
      
      // Check if email already exists
      const { data: existing } = await supabase
        .from('companies')
        .select('id')
        .eq('email', cleanEmail)
        .maybeSingle();

      if (existing) {
        setErrorMsg('Ya existe una empresa registrada con ese correo electrónico.');
        setRegisterLoading(false);
        return;
      }

      // Insert new company
      const newCompanyPayload = {
        name: registerForm.name.trim().toUpperCase(),
        email: cleanEmail,
        password_hash: registerForm.password.trim(),
        is_active: true,
        notes: 'Autorregistro simplificado desde Portal'
      };

      const { data: createdComp, error: insertErr } = await supabase
        .from('companies')
        .insert([newCompanyPayload])
        .select('*')
        .single();

      if (insertErr) throw insertErr;

      // Auto-login newly registered company
      localStorage.setItem('gdt_active_company', JSON.stringify(createdComp));
      setRegisterSuccess('¡Empresa registrada con éxito! Redirigiendo al portal...');
      setTimeout(() => {
        setIsRegisterOpen(false);
        navigate('/portal/empresa');
      }, 1200);
    } catch (err) {
      console.error('Register error:', err);
      setErrorMsg(err.message || 'No se pudo completar el registro de la empresa.');
    } finally {
      setRegisterLoading(false);
    }
  };

  const handleResetPasswordSubmit = async (e) => {
    e.preventDefault();
    setErrorMsg('');
    setResetSuccess('');

    if (!resetForm.email.trim() || !resetForm.newPassword.trim()) {
      setErrorMsg('Complete el correo electrónico y la nueva contraseña.');
      return;
    }

    if (resetForm.newPassword !== resetForm.confirmNewPassword) {
      setErrorMsg('Las contraseñas no coinciden.');
      return;
    }

    setResetLoading(true);
    try {
      const cleanEmail = resetForm.email.trim().toLowerCase();

      let { data: compDataList, error: findErr } = await supabase
        .from('companies')
        .select('*')
        .ilike('email', cleanEmail);
      if (findErr) throw findErr;

      let compData = (compDataList || []).find(c => (c.email || '').trim().toLowerCase() === cleanEmail);
      if (compData && resetForm.cuit.trim()) {
        if ((compData.cuit || '').trim() !== resetForm.cuit.trim()) compData = null;
      }

      if (!compData) {
        setErrorMsg('No se encontró ninguna empresa registrada con ese correo ' + (resetForm.cuit.trim() ? 'y CUIT.' : '.'));
        setResetLoading(false);
        return;
      }

      // Update password
      const { error: updateErr } = await supabase
        .from('companies')
        .update({ password_hash: resetForm.newPassword.trim() })
        .eq('id', compData.id);

      if (updateErr) throw updateErr;

      setResetSuccess('¡Contraseña actualizada con éxito! Ya puedes iniciar sesión.');
      setEmail(cleanEmail);
      setPassword(resetForm.newPassword.trim());
      setTimeout(() => {
        setIsResetOpen(false);
      }, 1500);
    } catch (err) {
      console.error('Reset password error:', err);
      setErrorMsg('No se pudo restablecer la contraseña.');
    } finally {
      setResetLoading(false);
    }
  };

  if (MAINTENANCE_CONFIG.enabled) {
    return (
      <div className="company-login-container" style={{ position: 'relative', overflow: 'hidden' }}>
        <div style={{
          position: 'fixed',
          top: 0, left: 0, right: 0, bottom: 0,
          zIndex: 999999,
          background: 'rgba(10, 15, 30, 0.96)',
          backdropFilter: 'blur(12px)',
          WebkitBackdropFilter: 'blur(12px)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '20px',
          userSelect: 'none'
        }}>
          <div style={{
            maxWidth: '540px',
            width: '100%',
            background: 'rgba(18, 24, 43, 0.96)',
            border: '1px solid rgba(0, 229, 255, 0.35)',
            borderRadius: '24px',
            padding: '40px 32px',
            textAlign: 'center',
            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.85), 0 0 35px rgba(0, 229, 255, 0.15)',
            color: '#ffffff'
          }}>
            <div style={{
              width: '76px',
              height: '76px',
              borderRadius: '50%',
              background: 'rgba(0, 229, 255, 0.12)',
              border: '1px solid rgba(0, 229, 255, 0.3)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 24px auto',
              color: '#00e5ff'
            }}>
              <Wrench size={38} />
            </div>

            <h2 style={{ fontSize: '1.6rem', fontWeight: '700', color: '#00e5ff', marginBottom: '14px', letterSpacing: '-0.5px' }}>
              Portal Empresas en Mantenimiento
            </h2>

            <p style={{ fontSize: '0.98rem', color: '#cbd5e1', lineHeight: '1.6', marginBottom: '20px' }}>
              Estimada institución / empresa: El formulario de gestión y solicitud de turnos se encuentra momentáneamente deshabilitado por tareas de mantenimiento.
            </p>

            <div style={{
              background: 'rgba(0, 229, 255, 0.06)',
              borderLeft: '4px solid #00e5ff',
              padding: '14px 18px',
              borderRadius: '10px',
              textAlign: 'left',
              fontSize: '0.88rem',
              color: '#94a3b8',
              lineHeight: '1.5',
              marginBottom: '28px'
            }}>
              <strong style={{ color: '#00e5ff' }}>ℹ️ Información sobre su atención:</strong><br />
              Por favor, intente nuevamente más tarde o comuníquese directamente con la administración para coordinar sus turnos corporativos.
            </div>

            <div style={{ fontSize: '0.82rem', color: '#64748b', fontWeight: '500' }}>
              GESTION DE TURNOS • Portal Empresas
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="company-login-container">
      <div className="company-login-card glass-modal-premium page-animate">
        <div className="company-login-header">
          <div className="logo-icon-box">
            <Building2 size={32} color="var(--primary-cyan)" />
          </div>
          <h1>GDT | PORTAL EMPRESAS</h1>
          <p>Plataforma exclusiva de gestión de turnos para empresas e instituciones registradas</p>
        </div>

        {errorMsg && (
          <div className="login-error-alert">
            <AlertCircle size={18} />
            <span>{errorMsg}</span>
          </div>
        )}

        <form onSubmit={handleLogin} className="company-login-form">
          <div className="form-group">
            <label>CORREO ELECTRÓNICO *</label>
            <div className="input-with-icon">
              <Mail size={18} className="input-icon" />
              <input 
                type="email" 
                required 
                placeholder="empresa@ejemplo.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>
          </div>

          <div className="form-group">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.3rem' }}>
              <label style={{ margin: 0 }}>CONTRASEÑA *</label>
              <button 
                type="button" 
                className="btn-link-action"
                onClick={() => { setErrorMsg(''); setResetSuccess(''); setIsResetOpen(true); }}
              >
                ¿Olvidó su contraseña?
              </button>
            </div>
            <div className="input-with-icon">
              <Lock size={18} className="input-icon" />
              <input 
                type="password" 
                required 
                placeholder="••••••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
            </div>
          </div>

          <button type="submit" className="btn-company-login" disabled={loading}>
            {loading ? (
              <>
                <RefreshCw size={18} className="animate-spin" />
                <span>INGRESANDO...</span>
              </>
            ) : (
              <>
                <span>INGRESAR AL PORTAL</span>
                <ArrowRight size={18} />
              </>
            )}
          </button>
        </form>

        <div className="company-register-prompt">
          <p>¿Su empresa aún no tiene cuenta?</p>
          <button 
            type="button" 
            className="btn-register-company-trigger"
            onClick={() => { setErrorMsg(''); setRegisterSuccess(''); setIsRegisterOpen(true); }}
          >
            <UserPlus size={16} /> Registrar mi Empresa
          </button>
        </div>

        <div className="company-login-footer">
          <ShieldCheck size={16} />
          <span>Acceso seguro para empresas autorizadas por Clínica Médica GDT</span>
        </div>
      </div>

      {/* REGISTER NEW COMPANY MODAL */}
      {isRegisterOpen && (
        <div className="appointment-modal-overlay" style={{ zIndex: 10000 }}>
          <div className="appointment-modal-container glass-modal-premium" style={{ maxWidth: '520px' }}>
            <div className="appointment-modal-header premium-header">
              <div className="header-title">
                <div className="title-icon-wrapper cyan"><Building2 size={20} /></div>
                <span>REGISTRAR NUEVA EMPRESA</span>
              </div>
              <button className="btn-close-modal" onClick={() => setIsRegisterOpen(false)}><X size={20} /></button>
            </div>

            <form onSubmit={handleRegisterCompany} style={{ padding: '1.5rem', display: 'flex', flexDirection: 'column', gap: '1.2rem' }}>
              {errorMsg && (
                <div className="login-error-alert">
                  <AlertCircle size={18} />
                  <span>{errorMsg}</span>
                </div>
              )}

              {registerSuccess && (
                <div className="login-error-alert" style={{ background: 'rgba(0, 229, 255, 0.1)', borderColor: '#00e5ff', color: '#00e5ff' }}>
                  <CheckCircle2 size={18} />
                  <span>{registerSuccess}</span>
                </div>
              )}

              <div className="form-group">
                <label style={{ color: 'var(--text-muted)', fontSize: '0.8rem', fontWeight: 600 }}>NOMBRE DE LA EMPRESA *</label>
                <input 
                  type="text" 
                  required 
                  placeholder="Ej: Clínica Sol S.A. / Empresa YPF"
                  value={registerForm.name}
                  onChange={(e) => setRegisterForm({ ...registerForm, name: e.target.value })}
                  style={{ width: '100%', padding: '0.8rem 1rem', background: 'rgba(255,255,255,0.05)', border: '1px solid var(--border-glass)', borderRadius: '10px', color: 'white' }}
                />
              </div>

              <div className="form-group">
                <label style={{ color: 'var(--text-muted)', fontSize: '0.8rem', fontWeight: 600 }}>CORREO ELECTRÓNICO DE LA EMPRESA *</label>
                <input 
                  type="email" 
                  required 
                  placeholder="ejemplo@empresa.com"
                  value={registerForm.email}
                  onChange={(e) => setRegisterForm({ ...registerForm, email: e.target.value })}
                  style={{ width: '100%', padding: '0.8rem 1rem', background: 'rgba(255,255,255,0.05)', border: '1px solid var(--border-glass)', borderRadius: '10px', color: 'white' }}
                />
              </div>

              <div className="form-grid-2" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                <div className="form-group">
                  <label style={{ color: 'var(--text-muted)', fontSize: '0.8rem', fontWeight: 600 }}>CREAR CONTRASEÑA *</label>
                  <input 
                    type="password" 
                    required 
                    placeholder="••••••••••••"
                    value={registerForm.password}
                    onChange={(e) => setRegisterForm({ ...registerForm, password: e.target.value })}
                    style={{ width: '100%', padding: '0.8rem 1rem', background: 'rgba(255,255,255,0.05)', border: '1px solid var(--border-glass)', borderRadius: '10px', color: 'white' }}
                  />
                </div>

                <div className="form-group">
                  <label style={{ color: 'var(--text-muted)', fontSize: '0.8rem', fontWeight: 600 }}>CONFIRMAR CONTRASEÑA *</label>
                  <input 
                    type="password" 
                    required 
                    placeholder="••••••••••••"
                    value={registerForm.confirmPassword}
                    onChange={(e) => setRegisterForm({ ...registerForm, confirmPassword: e.target.value })}
                    style={{ width: '100%', padding: '0.8rem 1rem', background: 'rgba(255,255,255,0.05)', border: '1px solid var(--border-glass)', borderRadius: '10px', color: 'white' }}
                  />
                </div>
              </div>

              <div className="appointment-modal-footer" style={{ marginTop: '1rem', padding: '1rem 0 0 0', display: 'flex', justifyContent: 'flex-end', gap: '0.75rem' }}>
                <button type="button" className="btn-cancel-modal" onClick={() => setIsRegisterOpen(false)}>CANCELAR</button>
                <button type="submit" className="btn-save-appointment" disabled={registerLoading}>
                  {registerLoading ? <RefreshCw className="animate-spin" size={16} /> : <UserPlus size={16} />}
                  <span>REGISTRAR Y CONTINUAR</span>
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* RESET PASSWORD MODAL */}
      {isResetOpen && (
        <div className="appointment-modal-overlay" style={{ zIndex: 10000 }}>
          <div className="appointment-modal-container glass-modal-premium" style={{ maxWidth: '480px' }}>
            <div className="appointment-modal-header premium-header">
              <div className="header-title">
                <div className="title-icon-wrapper cyan"><KeyRound size={20} /></div>
                <span>RESTABLECER CONTRASEÑA DE EMPRESA</span>
              </div>
              <button className="btn-close-modal" onClick={() => setIsResetOpen(false)}><X size={20} /></button>
            </div>

            <form onSubmit={handleResetPasswordSubmit} style={{ padding: '1.5rem', display: 'flex', flexDirection: 'column', gap: '1.2rem' }}>
              {errorMsg && (
                <div className="login-error-alert">
                  <AlertCircle size={18} />
                  <span>{errorMsg}</span>
                </div>
              )}

              {resetSuccess && (
                <div className="login-error-alert" style={{ background: 'rgba(0, 229, 255, 0.1)', borderColor: '#00e5ff', color: '#00e5ff' }}>
                  <CheckCircle2 size={18} />
                  <span>{resetSuccess}</span>
                </div>
              )}

              <p style={{ fontSize: '0.85rem', color: 'var(--text-muted)', margin: 0 }}>
                Ingrese el correo electrónico registrado de su empresa para generar una nueva contraseña de acceso:
              </p>

              <div className="form-group">
                <label style={{ color: 'var(--text-muted)', fontSize: '0.8rem', fontWeight: 600 }}>CORREO ELECTRÓNICO REGISTRADO *</label>
                <input 
                  type="email" 
                  required 
                  placeholder="empresa@ejemplo.com"
                  value={resetForm.email}
                  onChange={(e) => setResetForm({ ...resetForm, email: e.target.value })}
                  style={{ width: '100%', padding: '0.8rem 1rem', background: 'rgba(255,255,255,0.05)', border: '1px solid var(--border-glass)', borderRadius: '10px', color: 'white' }}
                />
              </div>

              <div className="form-group">
                <label style={{ color: 'var(--text-muted)', fontSize: '0.8rem', fontWeight: 600 }}>CUIT (OPCIONAL DE VERIFICACIÓN)</label>
                <input 
                  type="text" 
                  placeholder="30-12345678-9"
                  value={resetForm.cuit}
                  onChange={(e) => setResetForm({ ...resetForm, cuit: e.target.value })}
                  style={{ width: '100%', padding: '0.8rem 1rem', background: 'rgba(255,255,255,0.05)', border: '1px solid var(--border-glass)', borderRadius: '10px', color: 'white' }}
                />
              </div>

              <div className="form-group">
                <label style={{ color: 'var(--text-muted)', fontSize: '0.8rem', fontWeight: 600 }}>NUEVA CONTRASEÑA *</label>
                <input 
                  type="password" 
                  required 
                  placeholder="••••••••••••"
                  value={resetForm.newPassword}
                  onChange={(e) => setResetForm({ ...resetForm, newPassword: e.target.value })}
                  style={{ width: '100%', padding: '0.8rem 1rem', background: 'rgba(255,255,255,0.05)', border: '1px solid var(--border-glass)', borderRadius: '10px', color: 'white' }}
                />
              </div>

              <div className="form-group">
                <label style={{ color: 'var(--text-muted)', fontSize: '0.8rem', fontWeight: 600 }}>CONFIRMAR NUEVA CONTRASEÑA *</label>
                <input 
                  type="password" 
                  required 
                  placeholder="••••••••••••"
                  value={resetForm.confirmNewPassword}
                  onChange={(e) => setResetForm({ ...resetForm, confirmNewPassword: e.target.value })}
                  style={{ width: '100%', padding: '0.8rem 1rem', background: 'rgba(255,255,255,0.05)', border: '1px solid var(--border-glass)', borderRadius: '10px', color: 'white' }}
                />
              </div>

              <div className="appointment-modal-footer" style={{ marginTop: '1rem', padding: '1rem 0 0 0', display: 'flex', justifyContent: 'flex-end', gap: '0.75rem' }}>
                <button type="button" className="btn-cancel-modal" onClick={() => setIsResetOpen(false)}>CANCELAR</button>
                <button type="submit" className="btn-save-appointment" disabled={resetLoading}>
                  {resetLoading ? <RefreshCw className="animate-spin" size={16} /> : <KeyRound size={16} />}
                  <span>GUARDAR CONTRASEÑA</span>
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default CompanyLogin;
